import React from 'react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { User, MapPin, Building2 } from 'lucide-react';
import { Lead, LeadPipelineStage } from '../../../types';
import { LeadCategoryBadge } from '../../../components/LeadCategoryBadge';
import { formatDateToDDMMYYYY } from '../../../utils/date';
import { useHorizontalScrollTarget } from '../../../hooks/useHorizontalWheelScroll';
import { DockedHorizontalScrollbar } from '../../../components/DockedHorizontalScrollbar';
import {
  DND_TERMINAL_LOST_ID,
  DND_TERMINAL_WON_ID,
  DND_TERMINAL_WON_ID_LEGACY,
} from '../../../utils/leadPipeline';
import { leadListAmount, projectValueTooltip } from '../utils/projectValue';

const formatNetEuro = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Kanban-Karten sind schmaler als der Rest der App, deshalb liegt die Meta-Zeile
 * eine Stufe unter `text-xs`. Bewusst eine einzige Stufe — vorher standen hier
 * 9/10/11px nebeneinander.
 */
const CARD_META_TEXT = 'text-[11px]';

interface LeadCardProps {
  lead: Lead;
  onSelect?: (lead: Lead) => void;
  isOverlay?: boolean;
  isDragging?: boolean;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onSelect, isOverlay, isDragging }) => {
  const cleanMeta = (raw: unknown) => {
    const v = String(raw ?? '').trim();
    if (!v) return '';
    const normalized = v.toLowerCase();
    if (normalized === 'null' || normalized === 'undefined' || normalized === '-' || v === '—') return '';
    return v;
  };
  const nameText = cleanMeta(lead.name);
  const companyText = cleanMeta(lead.contact?.company);
  const addressText = cleanMeta(lead.address);

  return (
    <div
      className={`deal-card group touch-none ${isOverlay ? 'shadow-xl border-pipedrive-blue scale-105 rotate-2' : ''} ${isDragging ? 'opacity-30' : 'opacity-100'}`}
      onClick={() => onSelect?.(lead)}
    >
      <div className="flex justify-between items-start gap-2 mb-1 pointer-events-none">
        <div className="flex min-w-0 flex-col">
          <span className={`mb-0.5 text-app-text-subtle ${CARD_META_TEXT}`}>
            {lead.display_id}
          </span>
          <h4 className="text-sm font-semibold leading-tight text-app-text transition-colors group-hover:text-pipedrive-blue">
            {lead.lead_title}
          </h4>
        </div>
        <LeadCategoryBadge
          label={lead.category}
          color={lead.category_color}
          size="xs"
          variant="dot"
          className="shrink-0 max-w-[5.5rem]"
        />
      </div>

      <div className="space-y-1.5 pointer-events-none">
        {nameText ? (
          <div className={`flex items-center gap-2 text-app-text-subtle ${CARD_META_TEXT}`}>
            <User className="w-3 h-3 shrink-0" />
            <span className="truncate">{nameText}</span>
          </div>
        ) : null}
        {companyText ? (
          <div className={`flex items-center gap-2 text-app-text-subtle ${CARD_META_TEXT}`}>
            <Building2 className="w-3 h-3 shrink-0" />
            <span className="truncate" title={companyText}>
              {companyText}
            </span>
          </div>
        ) : null}
        {addressText ? (
          <div className={`flex items-center gap-2 text-app-text-subtle ${CARD_META_TEXT}`}>
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{addressText}</span>
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-app-border/70 pt-3 pointer-events-none">
        <span className={`text-app-text-subtle ${CARD_META_TEXT}`}>
          {formatDateToDDMMYYYY(lead.created_at.slice(0, 10))}
        </span>
        <span
          className={`shrink-0 tabular-nums text-app-text-muted ${CARD_META_TEXT}`}
          title={lead.project_value ? projectValueTooltip(lead.project_value) : undefined}
        >
          {formatNetEuro(leadListAmount(lead))}
        </span>
      </div>
    </div>
  );
};

interface DraggableCardProps {
  lead: Lead;
  onSelect: (lead: Lead) => void;
}

const DraggableCard: React.FC<DraggableCardProps> = ({ lead, onSelect }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id.toString(),
    data: lead,
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}>
      <LeadCard lead={lead} onSelect={onSelect} isDragging={isDragging} />
    </div>
  );
};

interface DroppableColumnProps {
  stage: LeadPipelineStage;
  leads: Lead[];
  onSelectLead: (lead: Lead) => void;
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({ stage, leads, onSelectLead }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column transition-colors ${isOver ? 'bg-blue-50/80 ring-2 ring-pipedrive-blue ring-inset' : ''}`}
    >
      <div className="px-2 py-1 mb-2">
        <h3 className="flex items-center justify-between text-sm font-semibold text-app-text-muted">
          {stage.label}
          <span className={`font-medium tabular-nums text-app-text-subtle ${CARD_META_TEXT}`}>
            {leads.length}
          </span>
        </h3>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-app-border">
          <div
            className="h-full bg-pipedrive-blue transition-all"
            style={{ width: `${Math.min(100, (leads.length / 5) * 100)}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {leads.map((lead) => (
          <DraggableCard key={lead.id} lead={lead} onSelect={onSelectLead} />
        ))}
        {leads.length === 0 && (
          <div className="border-2 border-dashed border-app-border rounded-lg h-24 flex items-center justify-center">
            <span className={`font-medium text-app-text-subtle ${CARD_META_TEXT}`}>Keine Projekte</span>
          </div>
        )}
      </div>
    </div>
  );
};

function TerminalDropZone({ id, label, tone }: { id: string; label: string; tone: 'won' | 'lost' }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const bg =
    tone === 'won'
      ? isOver
        ? 'bg-emerald-200 ring-2 ring-emerald-600'
        : 'bg-emerald-50 border-emerald-200'
      : isOver
        ? 'bg-red-200 ring-2 ring-red-600'
        : 'bg-red-50 border-red-200';

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-h-[52px] rounded-lg border-2 border-dashed flex items-center justify-center text-sm font-semibold transition-colors ${bg} ${
        tone === 'won' ? 'text-emerald-800' : 'text-red-800'
      }`}
    >
      {label}
    </div>
  );
}

export type PipelineMoveTarget =
  | { kind: 'stage'; stageId: string }
  | { kind: 'outcome'; outcome: 'won' | 'lost' };

function dropTargetToMove(overId: string): PipelineMoveTarget | null {
  if (overId === DND_TERMINAL_WON_ID || overId === DND_TERMINAL_WON_ID_LEGACY) {
    return { kind: 'outcome', outcome: 'won' };
  }
  if (overId === DND_TERMINAL_LOST_ID) return { kind: 'outcome', outcome: 'lost' };
  if (!overId) return null;
  return { kind: 'stage', stageId: overId };
}

interface PipelineBoardProps {
  leads: Lead[];
  stages: LeadPipelineStage[];
  onSelectLead: (lead: Lead) => void;
  onMoveLead: (id: string | number, target: PipelineMoveTarget) => void;
}

export const PipelineBoard: React.FC<PipelineBoardProps> = ({
  leads,
  stages,
  onSelectLead,
  onMoveLead,
}) => {
  const [activeLead, setActiveLead] = React.useState<Lead | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const openLeads = React.useMemo(
    () => leads.filter((l) => (l.outcome ?? 'open') === 'open'),
    [leads],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const lead = openLeads.find((l) => String(l.id) === String(active.id));
    if (lead) setActiveLead(lead);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event;
    setActiveLead(null);

    if (!over || active.id === over.id) return;
    const draggedLead = openLeads.find((l) => String(l.id) === String(active.id));
    if (!draggedLead) return;
    const mapped = dropTargetToMove(String(over.id));
    if (mapped) onMoveLead(draggedLead.id, mapped);
  };

  // Das Board ist breiter als der Viewport — Shift+Rad ersetzt das Ziehen der Leiste.
  const boardScroll = useHorizontalScrollTarget<HTMLDivElement>();

  const columns = stages.map((stage) => ({
    stage,
    leads: openLeads.filter(
      (l) =>
        (l.stage_id != null && String(l.stage_id) === stage.id) ||
        (l.stage_id == null && l.status === stage.label),
    ),
  }));

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="relative flex flex-col">
        <div
          ref={boardScroll.ref}
          className="flex gap-4 overflow-x-auto app-scrollbar items-start pb-2"
        >
          {columns.map((column) => (
            <DroppableColumn
              key={column.stage.id}
              stage={column.stage}
              leads={column.leads}
              onSelectLead={onSelectLead}
            />
          ))}
        </div>
        <DockedHorizontalScrollbar target={boardScroll.node} />

        {activeLead ? (
          <div className="fixed inset-x-0 bottom-0 z-[80] pointer-events-none px-4 pb-4 pt-10 bg-gradient-to-t from-slate-900/25 to-transparent">
            <div className="max-w-4xl mx-auto flex gap-3 pointer-events-auto">
              <TerminalDropZone id={DND_TERMINAL_WON_ID} label="Hier ablegen: Gewonnen" tone="won" />
              <TerminalDropZone id={DND_TERMINAL_LOST_ID} label="Hier ablegen: Verloren" tone="lost" />
            </div>
          </div>
        ) : null}
      </div>

      <DragOverlay
        dropAnimation={{
          sideEffects: defaultDropAnimationSideEffects({
            styles: {
              active: {
                opacity: '0.5',
              },
            },
          }),
        }}
      >
        {activeLead ? <LeadCard lead={activeLead} isOverlay /> : null}
      </DragOverlay>
    </DndContext>
  );
};
